/**
 * `/leads/new` — the lead-capture form (build item 9, docs/plan.md → Screens,
 * Capture / Edit). Capture is a full-bleed form in the design (`isCapture`),
 * so it keeps `hideShellChrome` set by the Wave-1 placeholder this replaces.
 *
 * Two things can pre-seed `useCaptureDraft` before this route ever renders,
 * and each is resolved differently on mount:
 *
 *   - A catalogue hand-off (the catalogue's own `+ Add`, or product-detail's
 *     `+ Add to a buyer`) adds a product and navigates straight here, marking
 *     `capture-draft.ts`'s hand-off flag as it goes. `consumeHandoff()` below
 *     reads (and clears) that flag — true means keep the seeded items.
 *   - An "Edit and retry" from `/sync` (docs/plan.md → "When a queued lead
 *     cannot be sent") sets a resume target via `setResumeTarget` and
 *     navigates here. `takeResumeTarget()` reads (and clears) it; when set,
 *     the queued outbox entry is loaded and used to prefill the form fields,
 *     the capture draft's items, and (if one was captured) the visiting card
 *     — see `outbox.ts`'s "Edit and retry hand-off" section.
 *
 * Neither case is present on a genuinely fresh `/leads/new` open, so any
 * items still sitting in the draft then are stray — most commonly an
 * in-progress `/leads/:id/edit` abandoned via the phone's hardware/gesture
 * Back rather than the in-app Cancel (which clears the draft itself). Those
 * get cleared here rather than leaking into the next lead.
 */
import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  CreateLeadInput,
  DIRECT_ENQUIRIES_EVENT_ID,
  normalizeMoneyInput,
  type CreateLeadItemInput
} from '@paradise/shared';

import {
  LeadCaptureForm,
  type LeadCaptureSaveResult,
  type LeadCaptureSubmission
} from '@/components/capture/lead-capture-form';
import { toCreateLeadItemInput } from '@/components/capture/item-mapping';
import { INDIA_DIALLING_CODE, valuesFromCreateInput, type LeadFormValues } from '@/components/capture/lead-form-values';
import { emptyToNull, phoneToNull } from '@/components/capture/money-input';
import { useSessionStore } from '@/lib/auth/session';
import { consumeHandoff, useCaptureDraft, type DraftItem } from '@/lib/capture-draft';
import { db, type LeadCreatePayload, type OutboxRow } from '@/lib/db';
import { newUuid } from '@/lib/uuid';
import { Spinner } from '@/components/ui/spinner';
import {
  discardOutboxEntry,
  enqueueLeadCreate,
  getOutboxEntry,
  takeResumeTarget,
  type CardCapture
} from '@/lib/offline';

export const Route = createFileRoute('/_app/leads/new')({
  staticData: { hideShellChrome: true },
  component: NewLeadRoute
});

const DEFAULT_VALUES: LeadFormValues = {
  company: '',
  contactName: '',
  // Prefilled with the dialling code for the same reason `country` is prefilled
  // 'India' — and because 86 of the first 143 leads stored a bare 10-digit
  // number nobody could dial from abroad. `phoneToNull` drops this back to null
  // if the user never types digits after it.
  phone: INDIA_DIALLING_CODE,
  city: '',
  // Prefilled, not a database default: ~95% of leads are Indian buyers, and an
  // unset country is what left 102 of the first 143 leads with no location at
  // all. A form default only — reopening an older lead must never silently
  // acquire a country nobody typed.
  country: 'India',
  email: '',
  // Overridden per-render from the session's `currentEventId` (else the
  // standing Direct Enquiries bucket) — see NewLeadRoute. The empty string
  // here is only the shape's placeholder; a fresh form never actually opens
  // with no event selected.
  eventId: '',
  stage: 'ENQUIRING',
  stageNote: '',
  advance: '',
  paymentMode: null,
  notes: '',
  followUpDate: ''
};

/** Narrows an outbox row to its `lead.create` payload — the only kind "Edit and retry" targets. */
function isLeadCreateEntry(entry: OutboxRow): entry is OutboxRow & { payload: LeadCreatePayload } {
  return entry.kind === 'lead.create';
}

/**
 * Rehydrates a queued `CreateLeadItemInput[]` (the "Edit and retry" payload)
 * back into capture-draft `DraftItem`s. The wire shape carries no
 * `itemNo`/`coverPath` — those are catalogue display fields, not persisted
 * per line item — so each is looked up from the offline-precached Dexie
 * `catalogue` mirror, same as `$leadId/edit.tsx`'s prefill. A product that
 * has since been deleted falls back to itemNo 0 / no photo rather than
 * blocking the resume.
 */
async function draftItemsFromQueuedItems(items: readonly CreateLeadItemInput[]): Promise<DraftItem[]> {
  return Promise.all(
    items.map(async item => {
      const product = await db.catalogue.get(item.productId);
      return {
        productId: item.productId,
        itemNo: product?.itemNo ?? 0,
        productNameSnapshot: item.productNameSnapshot,
        thumbPath: product?.photos[0]?.thumbPath ?? null,
        quantity: item.quantity,
        listPriceSnapshot: item.listPriceSnapshot ?? null,
        quotedPrice: item.quotedPrice ?? '',
        note: item.note ?? ''
      };
    })
  );
}

/** What a resolved "Edit and retry" resume hands the route: prefilled values, items already applied to the draft, and the original card sides (if any) to carry forward untouched. */
interface ResumedLead {
  values: LeadFormValues;
  cardFront: CardCapture | null;
  cardBack: CardCapture | null;
  cardFrontPreviewUrl: string | null;
  cardBackPreviewUrl: string | null;
}

async function saveNewLead(
  submission: LeadCaptureSubmission,
  resumeFront: CardCapture | null,
  resumeBack: CardCapture | null
): Promise<LeadCaptureSaveResult> {
  const { values, cardFront, cardBack, cardFrontRemoved, cardBackRemoved } = submission;

  if (values.eventId.trim() === '') {
    return { ok: false, message: 'Choose an event before saving.' };
  }

  const items = useCaptureDraft.getState().items.map(toCreateLeadItemInput);

  const input: CreateLeadInput = {
    clientUuid: newUuid(),
    eventId: values.eventId,
    items,
    company: values.company.trim(),
    contactName: emptyToNull(values.contactName),
    phone: phoneToNull(values.phone),
    email: emptyToNull(values.email),
    city: emptyToNull(values.city),
    country: emptyToNull(values.country),
    stage: values.stage,
    stageNote: emptyToNull(values.stageNote),
    advance: normalizeMoneyInput(values.advance),
    paymentMode: values.paymentMode,
    followUpDate: emptyToNull(values.followUpDate),
    notes: emptyToNull(values.notes)
  };

  const parsed = CreateLeadInput.safeParse(input);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const where = issue && issue.path.length > 0 ? `${String(issue.path[0])}: ` : '';
    return {
      ok: false,
      message: issue ? `${where}${issue.message}` : 'Check the form — something is not valid yet.'
    };
  }

  // A resumed "Edit and retry" already had card sides queued. If the user
  // hasn't touched a slot this time (no new capture, no explicit remove), carry
  // the original bytes forward rather than silently dropping them — this is a
  // fresh `clientUuid`/outbox entry, not a patch, so nothing else would resend.
  const frontToSend = cardFront ?? (cardFrontRemoved ? undefined : (resumeFront ?? undefined));
  const backToSend = cardBack ?? (cardBackRemoved ? undefined : (resumeBack ?? undefined));

  // Queues to the outbox and returns immediately; the sync engine drains it
  // whenever there is a connection. Never throws for "offline" — that is the
  // whole point of this call.
  await enqueueLeadCreate(parsed.data, frontToSend, backToSend);
  return { ok: true };
}

function NewLeadRoute() {
  const navigate = useNavigate();

  // The event to capture under, defaulted from the app bar's current
  // selection: a show when one is picked, else the standing Direct Enquiries
  // bucket (a normal buyer meeting / direct sales enquiry). The user can still
  // change it in the form. `currentEventId` is persisted in the offline
  // session mirror, so this default holds offline too.
  const currentEventId = useSessionStore(state => state.currentEventId);
  const freshDefaults: LeadFormValues = {
    ...DEFAULT_VALUES,
    eventId: currentEventId ?? DIRECT_ENQUIRIES_EVENT_ID
  };

  // Read once, synchronously, before the first paint — sessionStorage, so
  // this survives even a reload mid-navigation.
  const [resumeId] = useState(() => takeResumeTarget());
  const [resumed, setResumed] = useState<ResumedLead | null>(null);
  const [resumeLoading, setResumeLoading] = useState(resumeId !== null);
  const objectUrlsRef = useRef<string[]>([]);

  // Draft hygiene: run once. A hand-off pre-seeds the draft on purpose and
  // must survive; an "Edit and retry" resume seeds it itself, below. Anything
  // else left over is stray and must not leak into a fresh lead.
  useEffect(() => {
    if (resumeId) return;
    if (consumeHandoff()) return;
    useCaptureDraft.getState().clear();
    // Intentionally run once on mount only (`resumeId` is fixed at mount via
    // `useState(() => …)` above, so it never actually needs to redrive this).
  }, [resumeId]);

  useEffect(() => {
    if (!resumeId) return;
    let cancelled = false;
    void (async () => {
      const entry = await getOutboxEntry(resumeId);
      if (cancelled) return;

      if (!entry || !isLeadCreateEntry(entry)) {
        // Already discarded/retried elsewhere, or a stale target — degrade to
        // a blank form rather than crash. Still clear the draft: nothing here
        // is a legitimate hand-off either.
        useCaptureDraft.getState().clear();
        setResumeLoading(false);
        return;
      }

      const { input } = entry.payload;
      const [items, frontBlob, backBlob] = await Promise.all([
        draftItemsFromQueuedItems(input.items),
        db.cardBlobs.get(resumeId),
        db.cardBackBlobs.get(resumeId)
      ]);
      if (cancelled) return;

      useCaptureDraft.getState().setItems(items);

      const toResumedCard = (
        blob: { bytes: ArrayBuffer; contentType: string } | undefined
      ): { card: CardCapture | null; previewUrl: string | null } => {
        if (!blob) return { card: null, previewUrl: null };
        const previewUrl = URL.createObjectURL(new Blob([blob.bytes], { type: blob.contentType }));
        objectUrlsRef.current.push(previewUrl);
        return { card: { bytes: blob.bytes, contentType: blob.contentType }, previewUrl };
      };
      const frontResumed = toResumedCard(frontBlob);
      const backResumed = toResumedCard(backBlob);

      setResumed({
        values: valuesFromCreateInput(input),
        cardFront: frontResumed.card,
        cardBack: backResumed.card,
        cardFrontPreviewUrl: frontResumed.previewUrl,
        cardBackPreviewUrl: backResumed.previewUrl
      });
      setResumeLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [resumeId]);

  // The freshly-created preview URL is ours to revoke; `initialCardUrl` for a
  // non-resumed form is null, and edit mode's server URL is the parent's, not
  // ours (see LeadCaptureForm) — neither of those paths reach this ref.
  useEffect(() => {
    // The array itself is stable and only ever pushed to, so holding the
    // reference here still sees every URL added later.
    const urls = objectUrlsRef.current;
    return () => {
      for (const url of urls) URL.revokeObjectURL(url);
    };
  }, []);

  if (resumeLoading) {
    return (
      <div className="grid h-dvh place-items-center text-sm text-muted-foreground">
        <span className="flex items-center gap-2">
          <Spinner className="size-4" aria-hidden="true" /> Loading…
        </span>
      </div>
    );
  }

  return (
    <LeadCaptureForm
      mode="create"
      headTitle="New Buyer"
      defaultValues={resumed?.values ?? freshDefaults}
      initialCardFrontUrl={resumed?.cardFrontPreviewUrl ?? null}
      initialCardBackUrl={resumed?.cardBackPreviewUrl ?? null}
      saveLabel="Save"
      onCancel={() => void navigate({ to: '/' })}
      onSave={async submission => {
        const result = await saveNewLead(submission, resumed?.cardFront ?? null, resumed?.cardBack ?? null);
        if (result.ok) {
          // The old stuck entry is superseded by the fresh one just enqueued
          // above — discard it so it doesn't linger as a duplicate.
          if (resumeId) await discardOutboxEntry(resumeId);
          await navigate({ to: '/' });
        }
        return result;
      }}
    />
  );
}
